import React, { useEffect, useState } from "react";
import { fmtTime, fmtRange } from "@/lib/enrol";
import { api, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, X, Clock, Mail, Save } from "lucide-react";

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

export default function Availability() {
  const { user } = useAuth();
  const [mine, setMine] = useState({ availability: [], reminder_hours_before: 24, reminder_email: "" });
  const [team, setTeam] = useState([]);
  const [assign, setAssign] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const m = await api.get("/admin/me/availability"); setMine(m.data); } catch {}
    try { const t = await api.get("/admin/team-availability"); setTeam(t.data); } catch {}
    try { const a = await api.get("/admin/my-assignments"); setAssign(a.data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const addSlot = () => setMine({ ...mine, availability: [...(mine.availability || []), { _key: `slot-${Date.now()}`, day: "Mon", start: "09:00", end: "17:00" }] });
  const setSlot = (i, k, v) => setMine({ ...mine, availability: mine.availability.map((s, idx) => idx === i ? { ...s, [k]: v } : s) });
  const removeSlot = (i) => setMine({ ...mine, availability: mine.availability.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/me/availability", mine);
      toast.success("Availability saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]">
      <div className="mb-8">
        <div className="eyebrow mb-2">My schedule</div>
        <h1 className="font-display text-5xl uppercase">Availability</h1>
        <p className="text-zinc-400 mt-2 text-sm">Tell the team when you're free — used to schedule extras, cover sick calls, and send you reminders.</p>
      </div>

      {assign && (assign.sessions.length > 0 || assign.workshops.length > 0) && (
        <div className="card-surface p-6 mb-10" data-testid="my-assignments-card">
          <div className="eyebrow mb-3">My assignments</div>
          <div className="grid md:grid-cols-2 gap-3">
            {assign.sessions.map(s => (
              <div key={s.id} className="border-l-2 border-red-600 pl-3 py-2 text-sm" data-testid={`assign-session-${s.id}`}>
                <div className="font-bold">{s.batch_name}</div>
                <div className="text-zinc-400 text-xs">{s.day} · {fmtRange(s.start_time, s.end_time)} · {s.location_name || "—"}</div>
              </div>
            ))}
            {assign.workshops.map(w => (
              <div key={w.id} className="border-l-2 border-amber-500 pl-3 py-2 text-sm" data-testid={`assign-workshop-${w.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Workshop</div>
                <div className="font-bold">{w.name}</div>
                <div className="text-zinc-400 text-xs">{w.date} {fmtTime(w.start_time)} · {w.location_name || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-surface p-6 mb-10" data-testid="my-availability">
        <div className="flex items-center justify-between mb-4">
          <div className="eyebrow">My weekly windows</div>
          <button onClick={addSlot} className="text-xs text-red-500 uppercase tracking-widest flex items-center gap-1" data-testid="add-slot"><Plus size={12}/>Add window</button>
        </div>
        {(mine.availability || []).length === 0 && (
          <div className="text-zinc-500 text-sm py-6 text-center border border-dashed border-white/10">No windows yet — add your first.</div>
        )}
        <div className="space-y-3">
          {(mine.availability || []).map((s, i) => (
            <div key={s._key || `${s.day}-${s.start}-${s.end}-${i}`} className="flex items-center gap-2" data-testid={`slot-${i}`}>
              <select value={s.day} onChange={e => setSlot(i, "day", e.target.value)} className="bg-black border border-white/10 px-3 py-2 text-sm">
                {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <input type="time" value={s.start} onChange={e => setSlot(i, "start", e.target.value)} className="bg-black border border-white/10 px-3 py-2 text-sm" data-testid={`slot-start-${i}`}/>
              <span className="text-zinc-500">→</span>
              <input type="time" value={s.end} onChange={e => setSlot(i, "end", e.target.value)} className="bg-black border border-white/10 px-3 py-2 text-sm" data-testid={`slot-end-${i}`}/>
              <button onClick={() => removeSlot(i)} className="text-zinc-400 hover:text-red-500 p-2" data-testid={`slot-remove-${i}`}><X size={14}/></button>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-8 pt-6 border-t border-white/10">
          <div>
            <label className="eyebrow block mb-1">Reminder timing (hours before)</label>
            <input type="number" min={1} max={168} value={mine.reminder_hours_before || 24} onChange={e => setMine({ ...mine, reminder_hours_before: Number(e.target.value) })} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="reminder-hours"/>
            <div className="text-zinc-500 text-xs mt-1">e.g. 24 = day before. 2 = same-day nudge.</div>
          </div>
          <div>
            <label className="eyebrow block mb-1">Reminder email</label>
            <input type="email" value={mine.reminder_email || ""} placeholder={user?.email} onChange={e => setMine({ ...mine, reminder_email: e.target.value })} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="reminder-email"/>
            <div className="text-zinc-500 text-xs mt-1">Falls back to your login email if blank.</div>
          </div>
        </div>

        <button onClick={save} disabled={saving} className="btn-red mt-6 flex items-center gap-2" data-testid="save-availability">
          <Save size={14}/>{saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="mb-6">
        <div className="eyebrow mb-2">The team</div>
        <h2 className="font-display text-3xl uppercase">Who's around this week?</h2>
      </div>
      <div className="card-surface p-6 overflow-x-auto" data-testid="team-availability">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-zinc-500 uppercase tracking-widest text-xs">
            <th className="text-left px-3 py-2">Name</th>
            <th className="text-left px-3 py-2">Role</th>
            {DAYS.map(d => <th key={d} className="text-left px-3 py-2">{d}</th>)}
          </tr></thead>
          <tbody>
            {team.map(t => (
              <tr key={t.id} className="border-b border-white/5" data-testid={`team-row-${t.id}`}>
                <td className="px-3 py-3 font-semibold">{t.name || t.instructor_name || t.email}</td>
                <td className="px-3 py-3 text-xs uppercase tracking-widest text-zinc-500">{t.role}</td>
                {DAYS.map(d => {
                  const slots = (t.availability || []).filter(s => s.day === d);
                  return <td key={d} className="px-3 py-3 text-xs text-zinc-400">{slots.map(s => `${s.start}–${s.end}`).join(", ") || "—"}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
